// src/components/Contacts/ContactEditor.js
import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Grid,
    TextField,
    MenuItem,
    Typography,
    Link,
    Box,
    IconButton,
    Tooltip,
    Select,
    FormControl,
    InputLabel
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ContactEmailHistory from './ContactEmailHistory';

const URLInput = ({ label, value, onChange, name }) => (
    <Box sx={{ position: 'relative', width: '100%' }}>
        <TextField
            fullWidth
            label={label}
            name={name}
            value={value || ''}
            onChange={onChange}
            InputProps={{
                sx: {
                    paddingRight: '40px',
                    '& input': {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                    }
                }
            }}
        />
        {value && (
            <Tooltip title="Open in new window">
                <IconButton
                    size="small"
                    onClick={() => window.open(value, '_blank')}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: 'text.secondary',
                        backgroundColor: 'background.paper',
                        '&:hover': {
                            color: 'primary.main'
                        }
                    }}
                >
                    <OpenInNewIcon fontSize="small" />
                </IconButton>
            </Tooltip>
        )}
    </Box>
);

const INITIAL_FORM_STATE = {
    first_name: '',
    last_name: '',
    email: '',
    title: '',
    company_name: '',
    phone: '',
    priority: 1,
    linkedin_url: '',
    facebook_url: '',
    instagram_url: '',
    website: '',
    contact_snapshot: '',
    business_context: ''
};

const ContactEditor = ({ open, onClose, contact, onSave, campaignId }) => {
    const [formData, setFormData] = useState(INITIAL_FORM_STATE);
    const [selectedEmail, setSelectedEmail] = useState(null);
    const [openEmailDialog, setOpenEmailDialog] = useState(false);

    useEffect(() => {
        if (contact) {
            // Extract contact data, ensuring we get the right property path
            const contactData = contact.contact || contact;

            setFormData({
                first_name: contactData.first_name || '',
                last_name: contactData.last_name || '',
                email: contactData.email || '',
                title: contactData.title || '',
                company_name: contactData.company_name || '',
                phone: contactData.phone || '',
                priority: contactData.priority ?? 1,
                linkedin_url: contactData.linkedin_url || '',
                facebook_url: contactData.facebook_url || '',
                instagram_url: contactData.instagram_url || '',
                website: contactData.website || '',
                contact_snapshot: contactData.contact_snapshot || '',
                business_context: contactData.business_context || ''
            });
        } else {
            setFormData(INITIAL_FORM_STATE);
        }
    }, [contact]);

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(formData);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                {contact ? 'Edit Contact' : 'Add Contact'}
            </DialogTitle>
            <form onSubmit={handleSubmit}>
                <DialogContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="First Name"
                                name="first_name"
                                value={formData.first_name}
                                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Last Name"
                                name="last_name"
                                value={formData.last_name}
                                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Email"
                                name="email"
                                type="email"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Title"
                                name="title"
                                value={formData.title}
                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Company Name"
                                name="company_name"
                                value={formData.company_name}
                                onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Phone"
                                name="phone"
                                value={formData.phone}
                                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                select
                                label="Priority"
                                name="priority"
                                value={formData.priority || 2}
                                onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    priority: parseInt(e.target.value, 10)
                                }))}
                            >
                                <MenuItem value={1}>Low</MenuItem>
                                <MenuItem value={2}>Medium</MenuItem>
                                <MenuItem value={3}>High</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                                Social Media & Web Links
                            </Typography>
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                            <URLInput
                                label="Website"
                                name="website"
                                value={formData.website}
                                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                            />
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                            <URLInput
                                label="LinkedIn URL"
                                name="linkedin_url"
                                value={formData.linkedin_url}
                                onChange={(e) => setFormData({ ...formData, linkedin_url: e.target.value })}
                            />
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                            <URLInput
                                label="Facebook URL"
                                name="facebook_url"
                                value={formData.facebook_url}
                                onChange={(e) => setFormData({ ...formData, facebook_url: e.target.value })}
                            />
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                            <URLInput
                                label="Instagram URL"
                                name="instagram_url"
                                value={formData.instagram_url}
                                onChange={(e) => setFormData({ ...formData, instagram_url: e.target.value })}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                label="Contact Snapshot"
                                name="contact_snapshot"
                                value={formData.contact_snapshot}
                                onChange={(e) => setFormData({ ...formData, contact_snapshot: e.target.value })}
                                helperText="Key information about the contact for personalization"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                label="Business Context"
                                name="business_context"
                                value={formData.business_context}
                                onChange={(e) => setFormData({ ...formData, business_context: e.target.value })}
                                helperText="Information about their business, market position, and potential needs"
                            />
                        </Grid>

                    </Grid>
                </DialogContent>
                
                {contact && (
                    <DialogContent>
                        <Typography variant="h6" gutterBottom>
                            Message History
                        </Typography>
                        <ContactEmailHistory 
                            contactId={contact.id}
                            campaignId={campaignId}
                        />                        
                    </DialogContent>
                )}               
                <DialogActions>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button 
                        type="submit" 
                        variant="contained"
                        color="primary"
                    >
                        {contact ? 'Update' : 'Add'} Contact
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default ContactEditor;